import { Link } from "wouter";
import { Plus, Newspaper, Mail } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OutreachTabs } from "@/components/outreach/OutreachTabs";
import { useNewsletterIssues, type IssueRow } from "@/hooks/useNewsletter";
import { cn } from "@/lib/utils";

// Same Badge treatment as CampaignRow's STATUS_STYLE in Campaigns.tsx — these
// two pages sit one tab apart and should read as one product.
const STATUS_STYLE: Record<IssueRow["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
  sending: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
  paused: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
  completed: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  cancelled: "bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900",
};

function IssueRowView({ issue }: { issue: IssueRow }) {
  return (
    <Link href={`/campaigns/newsletter/${issue.id}/edit`}>
      <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 cursor-pointer">
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">{issue.name}</p>
          <p className="text-sm text-muted-foreground truncate">{issue.subject}</p>
        </div>
        <Badge variant="outline" className={cn("text-[10px] shrink-0", STATUS_STYLE[issue.status])}>
          {issue.status}
        </Badge>
        <div className="text-right tabular-nums text-sm w-24">
          <p>{issue.sent.toLocaleString()} sent</p>
          {issue.interest > 0 && (
            <p className="text-blue-600 dark:text-blue-400">{issue.interest} interested</p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function NewsletterIssues() {
  const { data: issues = [], isLoading } = useNewsletterIssues();
  const totalInterest = issues.reduce((s, i) => s + i.interest, 0);

  const stat = (value: number | string, label: string, tone = "") => (
    <div className="flex-1 min-w-[110px] bg-card border rounded-lg px-4 py-3">
      <p className={cn("text-xl font-semibold tabular-nums", tone)}>{value}</p>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mt-0.5">{label}</p>
    </div>
  );

  return (
    <AppLayout title="Newsletter">
      <div className="space-y-4 page-enter">
        <h1 className="sr-only">Newsletter</h1>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <OutreachTabs active="newsletter" />
          <div className="flex gap-2">
            <Link href="/campaigns/newsletter/lists"><Button variant="outline">Lists</Button></Link>
            <Link href="/campaigns/newsletter/new">
              <Button className="gap-1"><Plus className="w-4 h-4" /> New issue</Button>
            </Link>
          </div>
        </div>

        {issues.length > 0 && (
          <div className="flex gap-3 flex-wrap">
            {stat(issues.length, "Issues")}
            {stat(issues.reduce((s, i) => s + i.sent, 0).toLocaleString(), "Emails sent")}
            {stat(totalInterest, "Teaser requests", totalInterest > 0 ? "text-blue-600 dark:text-blue-400" : "")}
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-40" />
        ) : issues.length === 0 ? (
          <div className="text-center py-14 border rounded-lg">
            <Newspaper className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium">No issues yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Send your current deal pipeline to the buyer network. Pick the deals to feature, edit the anonymized cards, and send from one consistent mailbox.
            </p>
            <Link href="/campaigns/newsletter/new">
              <Button className="mt-4 gap-1"><Mail className="w-4 h-4" /> New issue</Button>
            </Link>
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {issues.map((i) => <IssueRowView key={i.id} issue={i} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
